import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  ShoppingBag,
  Ticket,
  LogOut
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

interface CustomerSidebarProps {
  isOpen: boolean;
}

const CustomerSidebar: React.FC<CustomerSidebarProps> = ({ isOpen }) => {
  const location = useLocation();
  const { signOut } = useAuth();

  const navigation = [
    {
      name: 'My Orders',
      href: '/customer-dashboard/orders',
      icon: ShoppingBag,
      current: location.pathname === '/customer-dashboard/orders'
    },
    {
      name: 'My Tickets',
      href: '/customer-dashboard/my-tickets',
      icon: Ticket,
      current: location.pathname === '/customer-dashboard/my-tickets'
    },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div
      className={cn(
        'bg-primary text-primary-foreground transition-all duration-300 ease-in-out fixed left-0 top-0 z-30 h-screen md:relative md:z-auto',
        isOpen ? 'w-64' : 'w-0 md:w-16'
      )}
    >
      <div className={cn(
        'flex flex-col h-full transition-opacity duration-300 overflow-hidden',
        isOpen ? 'opacity-100' : 'opacity-0 md:opacity-100'
      )}>
        {/* Logo/Brand */}
        <div className="flex items-center justify-center p-4 border-b border-primary/20 flex-shrink-0">
          <div className={cn(
            'text-xl font-bold text-primary-foreground transition-opacity duration-300',
            isOpen ? 'opacity-100' : 'opacity-0'
          )}>
            {isOpen && 'RecruitLook'}
          </div>
          {!isOpen && (
            <div className="hidden md:block w-8 h-8 bg-secondary rounded-full flex-shrink-0" />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200',
                  item.current
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-primary-foreground/80 hover:bg-primary/20 hover:text-primary-foreground'
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {isOpen && (
                  <span className="ml-3 transition-opacity duration-300">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sign Out */}
        <div className="p-4 border-t border-primary/20 flex-shrink-0">
          <Button
            onClick={handleSignOut}
            variant="ghost"
            className={cn(
              'w-full justify-start text-primary-foreground/80 hover:bg-primary/20 hover:text-primary-foreground',
              !isOpen && 'md:justify-center'
            )}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {isOpen && <span className="ml-3">Sign Out</span>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSidebar;